// API Configuration and Services for Super Wear Backend Integration

// Real API Configuration - No Mock Data
import { getApiBaseUrl, getApiUrl, getWhatsAppConfig } from '../config/environment';
import {
  CONTACT_CHANNELS_STORAGE_KEY,
  persistContactChannelsLocal,
  normalizeContactChannelsData,
} from '../utils/whatsappContact';
import { SITE_SETTINGS_STORAGE_KEY } from '../utils/siteBranding';
import { extractSheenBackendErrorMessageLoose } from '../utils/apiErrorMessage';
import { shouldSuppressAdminAccessDeniedToast } from '../admin/utils/adminApiError';
import {
  normalizeProductsArray,
  mergeProductListsById,
  parseTrendingByViewsResponse,
} from '../utils/productResponse';
import { stockColorKeyForApi, stockSizeKeyForApi } from '../utils/stockKeys';
import { normalizeAccountPayloadForApi } from '../utils/adminPaymentAccountMethod';
import { accountsArrayFromResponse } from '../utils/paymentAccountsResponse';
import {
  creatorProductMatchesIdentifier,
  isMongoObjectIdString,
  rememberCreatorSlug,
  normalizeCreatorSlugInput,
} from '../utils/creatorSlug';

/**
 * Stock size as sent on admin stock endpoints — preserves casing for DB alignment.
 */
function formatSizeForStockApi(size) {
  return stockSizeKeyForApi(size);
}

// API Configuration - use getter so baseURL works with network IP (172.28.x.x)
const apiConfig = {
  get baseURL() { return getApiBaseUrl(); },
  headers: {
    'Content-Type': 'application/json',
  },
};

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to set auth token
const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

// Helper function to get headers with auth
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    ...apiConfig.headers,
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Helper function to get admin headers
const getAdminHeaders = () => {
  const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  if (!adminToken) {
    console.warn('⚠️ No admin token found - admin routes require admin authentication');
  }
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
  };
};

// Global flag to prevent multiple authExpired events
let authExpiredDispatched = false;
let authExpiredTimeout = null;

// Generic API request function (customer / public routes)
const apiRequest = async (endpoint, options = {}) => {
  const silentLog = options.silentLog === true;
  try {
    let url = `${apiConfig.baseURL}${endpoint}`;
    
    if (!silentLog) {
      console.log('🔧 API Request URL:', url);
      console.log('🔧 API Base URL:', apiConfig.baseURL);
    }
    
    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined && options.params[key] !== null) {
          searchParams.append(key, options.params[key]);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const method = String(options.method || 'GET').toUpperCase();
    const token = getAuthToken();
    // Avoid `Content-Type: application/json` on GET/HEAD with no body so the browser may treat the
    // request as "simple" and skip an OPTIONS preflight (helps when API CORS on OPTIONS is misconfigured).
    // `Authorization` still triggers preflight when the user is logged in — backend must allow OPTIONS then.
    const simpleRead = (method === 'GET' || method === 'HEAD') && !options.body;
    const headers = {
      ...(simpleRead ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };
    
    const { silentLog: _sl, params: _params, ...fetchRest } = options;
    const config = {
      ...fetchRest,
      headers,
    };

    if (!silentLog) {
      console.log('🔧 Making API request to:', url);
      console.log('🔧 Request method:', config.method || 'GET');
      console.log('🔧 Request headers:', config.headers);
      if (config.body) {
        console.log('🔧 Request body:', config.body);
      }
    }

    const response = await fetch(url, config);
    
    // Handle different response types
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 401) {
        // Token expired or invalid - dispatch event only once
        setAuthToken(null);
        
        // Prevent multiple authExpired events
        if (!authExpiredDispatched) {
          authExpiredDispatched = true;
          window.dispatchEvent(new CustomEvent('authExpired'));
          
          // Reset flag after 5 seconds
          if (authExpiredTimeout) {
            clearTimeout(authExpiredTimeout);
          }
          authExpiredTimeout = setTimeout(() => {
            authExpiredDispatched = false;
          }, 5000);
        }
        
        // sheen-BE: { success, error } — often only `error`, no `message`
        const errorMessage =
          extractSheenBackendErrorMessageLoose(data, 401) ||
          'Token expired. Please login again.';
        throw new Error(JSON.stringify({ error: errorMessage }));
      }

      const errorMessage = extractSheenBackendErrorMessageLoose(
        data,
        response.status
      );
      const errPayload = { error: errorMessage };
      if (data && typeof data === 'object') {
        if (data.code != null && data.code !== '') errPayload.code = data.code;
        if (Array.isArray(data.availablePaymentMethods)) {
          errPayload.availablePaymentMethods = data.availablePaymentMethods;
        }
      }
      throw new Error(JSON.stringify(errPayload));
    }

    return data;
  } catch (error) {
    if (!silentLog) {
      console.error('API Request Error:', error);
    }
    throw error;
  }
};

// Admin-specific API request function
const adminApiRequest = async (endpoint, options = {}) => {
  try {
    let url = `${apiConfig.baseURL}${endpoint}`;
    
    // Debug: Log the admin API URL being used
    console.log('🔧 Admin API Request URL:', url);
    console.log('🔧 Admin API Base URL:', apiConfig.baseURL);
    
    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined && options.params[key] !== null) {
          searchParams.append(key, options.params[key]);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    const adminHeaders = getAdminHeaders();
    const adminToken = localStorage.getItem('adminToken');
    
    // Debug: Log token info
    console.log('🔑 Admin Token Present:', !!adminToken);
    if (adminToken) {
      try {
        const payload = JSON.parse(atob(adminToken.split('.')[1]));
        console.log('🔑 Admin Token Role:', payload.role || 'Not found in token');
        console.log('🔑 Admin Token User ID:', payload.userId || payload.id || 'Not found');
      } catch (e) {
        console.warn('⚠️ Could not decode admin token:', e);
      }
    }
    
    const config = {
      ...options,
      headers: {
        ...adminHeaders,
        ...options.headers,
      },
    };

    console.log('🔧 Making Admin API request to:', url);
    console.log('🔑 Admin headers:', config.headers);
    
    const response = await fetch(url, config);
    
    // Handle different response types
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 401) {
        // Admin token expired or invalid - dispatch event only once
        localStorage.removeItem('adminToken');
        localStorage.removeItem('authToken');
        
        // Prevent multiple authExpired events
        if (!authExpiredDispatched) {
          authExpiredDispatched = true;
          window.dispatchEvent(new CustomEvent('authExpired'));
          
          // Reset flag after 5 seconds
          if (authExpiredTimeout) {
            clearTimeout(authExpiredTimeout);
          }
          authExpiredTimeout = setTimeout(() => {
            authExpiredDispatched = false;
          }, 5000);
        }
        
        const errorMessage =
          extractSheenBackendErrorMessageLoose(data, 401) ||
          'Admin authentication expired. Please login again.';
        throw new Error(JSON.stringify({ error: errorMessage }));
      }

      // Handle 403 — team permission OR business rules (e.g. CANNOT_DELETE_ADMIN)
      if (response.status === 403) {
        const errMsg = extractSheenBackendErrorMessageLoose(data, 403);
        const permissionKey = typeof data === 'object' && data ? data.permissionKey : null;
        const code = typeof data === 'object' && data ? data.code : null;
        // Do not fire team-permission toast when backend blocks deleting an admin user
        if (
          code !== 'CANNOT_DELETE_ADMIN' &&
          !shouldSuppressAdminAccessDeniedToast(permissionKey, errMsg)
        ) {
          window.dispatchEvent(
            new CustomEvent('adminAccessDenied', {
              detail: { error: errMsg, permissionKey },
            })
          );
        }
        throw new Error(
          JSON.stringify({ error: errMsg, status: 403, permissionKey, code })
        );
      }

      // Handle 404 errors specifically
      if (response.status === 404) {
        const errorMessage = extractSheenBackendErrorMessageLoose(data, 404);
        console.error('❌ 404 Error - Endpoint not found:', url);
        console.error('❌ 404 Error - Response:', data);
        return {
          success: false,
          error: errorMessage,
          message: errorMessage,
          status: 404
        };
      }

      const errorMessage = extractSheenBackendErrorMessageLoose(
        data,
        response.status
      );
      throw new Error(JSON.stringify({ error: errorMessage }));
    }

    return data;
  } catch (error) {
    console.error('Admin API Request Error:', error);
    throw error;
  }
};

// Authentication Services
export const authService = {
  // Register new user
  register: async (userData) => {
    console.log('🔧 Register API - Request Data:', userData);
    console.log('🔧 Register API - Request URL:', `${apiConfig.baseURL}/auth/register`);
    
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    console.log('🔧 Register API - Response:', response);
    
    if (response.token) {
      setAuthToken(response.token);
    }
    
    return response;
  },

  /**
   * Signup: send WhatsApp verification code (Bird). Path keeps `whatsapp-otp` for backward compatibility.
   * POST /api/auth/signup/whatsapp-otp/send — body { phone } (alias: mobile)
   */
  sendSignupWhatsappOtp: async (phone) => {
    try {
      const res = await apiRequest('/auth/signup/whatsapp-otp/send', {
        method: 'POST',
        body: JSON.stringify({ phone: String(phone || '').trim() }),
        silentLog: true,
      });
      const ok = !(res && res.success === false);
      const msg =
        (typeof res?.message === 'string' && res.message.trim()) ||
        (typeof res?.data?.message === 'string' && res.data.message.trim()) ||
        'Verification code sent to your WhatsApp.';
      return { success: ok, message: msg, raw: res };
    } catch (error) {
      let msg = 'Failed to send verification code. Please try again.';
      try {
        const p = JSON.parse(error.message);
        if (p?.error) msg = String(p.error);
      } catch {
        /* ignore */
      }
      return { success: false, message: msg };
    }
  },

  /**
   * Signup: verify WhatsApp code (public). Path keeps `whatsapp-otp` for backward compatibility.
   * POST /api/auth/signup/whatsapp-otp/verify — body { phone, code } (alias: otp for code)
   */
  verifySignupWhatsappOtp: async ({ phone, code }) => {
    try {
      const res = await apiRequest('/auth/signup/whatsapp-otp/verify', {
        method: 'POST',
        body: JSON.stringify({
          phone: String(phone || '').trim(),
          code: String(code || '').trim(),
        }),
        silentLog: true,
      });
      const ok =
        res?.success === true ||
        res?.data?.verified === true ||
        res?.verified === true;
      const msg =
        (typeof res?.message === 'string' && res.message.trim()) ||
        (ok ? 'Mobile number verified.' : 'Invalid or expired code.');
      const phoneVerificationToken =
        (typeof res?.data?.phoneVerificationToken === 'string' &&
          res.data.phoneVerificationToken.trim()) ||
        (typeof res?.phoneVerificationToken === 'string' && res.phoneVerificationToken.trim()) ||
        null;
      return {
        success: !!ok,
        message: msg,
        raw: res,
        phoneVerificationToken,
      };
    } catch (error) {
      let msg = 'Verification failed. Please try again.';
      try {
        const p = JSON.parse(error.message);
        if (p?.error) msg = String(p.error);
      } catch {
        /* ignore */
      }
      return { success: false, message: msg, phoneVerificationToken: null };
    }
  },

  /**
   * Forgot password: send WhatsApp to an **existing** account phone (Bird).
   * POST /api/auth/forgotpassword/whatsapp-otp/send — body { phone } (alias: mobile)
   * Must NOT reject with "already registered"; only non-registered numbers should error.
   */
  sendForgotPasswordWhatsappOtp: async (phone) => {
    try {
      const res = await apiRequest('/auth/forgotpassword/whatsapp-otp/send', {
        method: 'POST',
        body: JSON.stringify({ phone: String(phone || '').trim() }),
        silentLog: true,
      });
      const explicitFail = res?.success === false;
      const explicitOk =
        res?.success === true ||
        res?.data?.success === true ||
        res?.data?.sent === true ||
        res?.sent === true;
      const ok = explicitOk || !explicitFail;
      const msg =
        (typeof res?.message === 'string' && res.message.trim()) ||
        (typeof res?.data?.message === 'string' && res.data.message.trim()) ||
        'Verification code sent to your WhatsApp.';
      return { success: ok, message: msg, raw: res };
    } catch (error) {
      let msg = 'Failed to send verification code. Please try again.';
      try {
        const p = JSON.parse(error.message);
        if (p?.error) msg = String(p.error);
      } catch {
        /* ignore */
      }
      return { success: false, message: msg };
    }
  },

  /**
   * Forgot password: verify WhatsApp code (public). Same response shape as signup verify.
   * POST /api/auth/forgotpassword/whatsapp-otp/verify — body { phone, code }
   */
  verifyForgotPasswordWhatsappOtp: async ({ phone, code }) => {
    try {
      const res = await apiRequest('/auth/forgotpassword/whatsapp-otp/verify', {
        method: 'POST',
        body: JSON.stringify({
          phone: String(phone || '').trim(),
          code: String(code || '').trim(),
        }),
        silentLog: true,
      });
      const ok =
        res?.success === true ||
        res?.data?.verified === true ||
        res?.verified === true;
      const msg =
        (typeof res?.message === 'string' && res.message.trim()) ||
        (ok ? 'Mobile number verified.' : 'Invalid or expired code.');
      const phoneVerificationToken =
        (typeof res?.data?.phoneVerificationToken === 'string' &&
          res.data.phoneVerificationToken.trim()) ||
        (typeof res?.phoneVerificationToken === 'string' && res.phoneVerificationToken.trim()) ||
        null;
      return {
        success: !!ok,
        message: msg,
        raw: res,
        phoneVerificationToken,
      };
    } catch (error) {
      let msg = 'Verification failed. Please try again.';
      try {
        const p = JSON.parse(error.message);
        if (p?.error) msg = String(p.error);
      } catch {
        /* ignore */
      }
      return { success: false, message: msg, phoneVerificationToken: null };
    }
  },

  // Login user
  login: async (credentials) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    if (response.token) {
      setAuthToken(response.token);
    }
    
    return response;
  },

  // Get current user
  getCurrentUser: async () => {
    return apiRequest('/auth/me');
  },

  // ✅ NEW: Get user profile data (RECOMMENDED API)
  getUserProfile: async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }

      console.log('🔄 API Service: Fetching user profile...');

      const response = await fetch(`${getApiBaseUrl()}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ API Service: User profile fetched:', data.user);
        return data.user;
      } else {
        throw new Error(data.message || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('❌ API Service: Error fetching user profile:', error);
      throw error;
    }
  },

  // Update user details - UNIFIED API ENDPOINT
  updateDetails: async (userData) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      console.log('🔄 API Service: Updating profile with data:', userData);

      const requestBody = {
        ...(userData.name !== undefined ? { name: userData.name } : {}),
        ...(userData.email !== undefined ? { email: userData.email } : {}),
        ...(userData.phone !== undefined ? { phone: userData.phone } : {}),
        ...(userData.phoneVerificationToken
          ? { phoneVerificationToken: userData.phoneVerificationToken }
          : {}),
        ...(userData.dateOfBirth !== undefined ? { dateOfBirth: userData.dateOfBirth } : {}),
        ...(userData.phoneNumbers !== undefined ? { phoneNumbers: userData.phoneNumbers } : {}),
        ...(userData.avatar !== undefined ? { avatar: userData.avatar } : {}),
        ...(userData.address || userData.city || userData.state || userData.postalCode || userData.country
          ? {
              addresses: [{
                addressLine1: userData.address || '',
                city: userData.city || '',
                state: userData.state || '',
                postalCode: userData.postalCode || '',
                country: userData.country || '',
                isDefault: true,
              }],
            }
          : {}),
      };

      console.log('📤 API Service: Sending request body to backend:', requestBody);

      const response = await fetch(`${getApiBaseUrl()}/auth/updatedetails`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 API Service: Response status:', response.status);
      console.log('📥 API Service: Response headers:', response.headers);

      const data = await response.json();
      console.log('📥 API Service: Response data:', data);
      console.log('📥 API Service: User addresses in response:', data.user?.addresses);
      
      if (response.ok && data.success) {
        console.log('✅ API Service: Profile updated successfully in database');
        
        // ✅ Update localStorage with new user data
        const currentUser = JSON.parse(localStorage.getItem('user') || localStorage.getItem('sheenwear_user') || '{}');
        const updatedUser = { ...currentUser, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        localStorage.setItem('sheenwear_user', JSON.stringify(updatedUser));
        
        return data;
      } else {
        console.error('❌ API Service: Profile update failed:', data);
        throw new Error(data.message || data.error || 'Profile update failed');
      }
    } catch (error) {
      console.error('❌ API Service: Profile update error:', error);
      throw error;
    }
  },

  // Change password
  changePassword: async (passwordData) => {
    return apiRequest('/auth/updatepassword', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  // Logout user
  logout: async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAuthToken(null);
    }
  },

  // Refresh token
  refreshToken: async () => {
    const response = await apiRequest('/auth/refresh-token', {
      method: 'POST',
    });
    
    if (response.token) {
      setAuthToken(response.token);
    }
    
    return response;
  },

  // Forgot password
  forgotPassword: async (email) => {
    return apiRequest('/auth/forgotpassword', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * After WhatsApp OTP is verified (`sendForgotPasswordOTP` / `verifyForgotPasswordOTP` → forgot whatsapp-otp routes),
   * submit the new password. Backend should implement:
   * POST /api/auth/forgotpassword/complete-phone
   * Body: { phone, password, phoneVerificationToken? } — token from signup verify response when JWT is enabled.
   * @returns {{ success: boolean, message?: string, error?: string }}
   */
  completeForgotPasswordWithPhoneOtp: async ({ phone, password, phoneVerificationToken }) => {
    try {
      const data = await apiRequest('/auth/forgotpassword/complete-phone', {
        method: 'POST',
        body: JSON.stringify({
          phone: String(phone || '').trim(),
          password: String(password || ''),
          ...(phoneVerificationToken
            ? { phoneVerificationToken: String(phoneVerificationToken).trim() }
            : {}),
        }),
        silentLog: true,
      });
      const success =
        data?.success === true ||
        data?.success === 'true' ||
        data?.data?.success === true;
      const message =
        (typeof data?.message === 'string' && data.message.trim()) ||
        (typeof data?.data?.message === 'string' && data.data.message.trim()) ||
        '';
      if (success) {
        return { success: true, message: message || 'Password updated successfully.' };
      }
      return {
        success: false,
        error:
          (typeof data?.error === 'string' && data.error.trim()) ||
          message ||
          'Could not reset password.',
      };
    } catch (error) {
      let err = 'Network error. Please try again.';
      try {
        const p = JSON.parse(error.message);
        if (p?.error) err = String(p.error);
      } catch {
        /* ignore */
      }
      return { success: false, error: err };
    }
  },

  // Reset password
  resetPassword: async (resetToken, password) => {
    return apiRequest(`/auth/resetpassword/${resetToken}`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },

  // Verify email
  verifyEmail: async (token) => {
    return apiRequest(`/auth/verify-email/${token}`, {
      method: 'GET',
    });
  },

  // Resend verification email
  resendVerification: async (email) => {
    return apiRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Admin login
  adminLogin: async (credentials) => {
    const response = await apiRequest('/auth/admin-login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    if (response.token) {
      setAuthToken(response.token);
    }
    
    return response;
  },
};

function extractUserOrdersList(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const nested = payload.data != null && typeof payload.data === 'object' ? payload.data : null;
  const candidates = [
    payload.orders,
    nested?.orders,
    Array.isArray(payload.data) ? payload.data : null,
    nested?.items,
    payload.items,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (Array.isArray(c)) return c;
  }
  return [];
}

/** Tries several storefront paths; avoids throwing so profile can show an empty list quietly. */
async function fetchUserOrdersListWithFallback() {
  const paths = ['/orders', '/orders/my-orders', '/me/orders', '/users/me/orders'];
  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    try {
      const res = await apiRequest(path, { method: 'GET', silentLog: true });
      const orders = extractUserOrdersList(res);
      if (res && res.success === false && orders.length === 0) continue;
      const success = res?.success !== false;
      return {
        ...res,
        success,
        orders,
        data: orders.length ? orders : res?.data ?? [],
      };
    } catch {
      /* try next */
    }
  }
  return { success: true, orders: [], data: [] };
}

// User Services
export const userService = {
  // Get user profile
  getProfile: async () => {
    return apiRequest('/auth/profile');
  },

  // Update user profile
  updateProfile: async (profileData) => {
    return apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  // Update password
  updatePassword: async (passwordData) => {
    return apiRequest('/users/password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  // Get user orders
  getOrders: async () => fetchUserOrdersListWithFallback(),

  // Get specific order
  getOrder: async (orderId) => {
    const id = encodeURIComponent(String(orderId));
    try {
      return await apiRequest(`/orders/my-orders/${id}`, { method: 'GET', silentLog: true });
    } catch {
      return apiRequest(`/orders/${id}`);
    }
  },

  // ✅ NEW: Address management endpoints
  // Get user addresses
  getAddresses: async () => {
    return apiRequest('/users/profile/me');
  },

  // Add new address - Use new dedicated endpoint
  addAddress: async (addressData) => {
    console.log('📍 API Service: Adding address with data:', addressData);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await fetch(`${getApiBaseUrl()}/users/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: addressData.type || 'home',
          addressLine1: addressData.addressLine1,
          addressLine2: addressData.addressLine2 || '',
          city: addressData.city,
          state: addressData.state || '',
          postalCode: addressData.postalCode || '',
          country: addressData.country || 'Pakistan',
          isDefault: addressData.isDefault || false
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ API Service: Address added successfully');
        return { success: true, data: data.data, message: data.message || 'Address added successfully' };
      } else {
        console.error('❌ API Service: Address addition failed:', data);
        throw new Error(data.message || data.error || 'Address addition failed');
      }
    } catch (error) {
      console.error('❌ API Service: Address addition error:', error);
      throw error;
    }
  },

  // Update address - Use new dedicated endpoint
  updateAddress: async (addressId, addressData) => {
    console.log('📍 API Service: Updating address with data:', addressData);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await fetch(`${getApiBaseUrl()}/users/addresses/${addressId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: addressData.type,
          addressLine1: addressData.addressLine1,
          addressLine2: addressData.addressLine2 || '',
          city: addressData.city,
          state: addressData.state || '',
          postalCode: addressData.postalCode || '',
          country: addressData.country || 'Pakistan',
          isDefault: addressData.isDefault || false
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ API Service: Address updated successfully');
        return { success: true, data: data.data, message: data.message || 'Address updated successfully' };
      } else {
        console.error('❌ API Service: Address update failed:', data);
        throw new Error(data.message || data.error || 'Address update failed');
      }
    } catch (error) {
      console.error('❌ API Service: Address update error:', error);
      throw error;
    }
  },

  // Delete address - Use new dedicated endpoint
  deleteAddress: async (addressId) => {
    console.log('📍 API Service: Deleting address with ID:', addressId);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await fetch(`${getApiBaseUrl()}/users/addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ API Service: Address deleted successfully');
        return { success: true, data: data.data, message: data.message || 'Address deleted successfully' };
      } else {
        console.error('❌ API Service: Address deletion failed:', data);
        throw new Error(data.message || data.error || 'Address deletion failed');
      }
    } catch (error) {
      console.error('❌ API Service: Address deletion error:', error);
      throw error;
    }
  },

  // Set default address - Use profile update endpoint
  setDefaultAddress: async (addressId) => {
    console.log('📍 API Service: Setting default address with ID:', addressId);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      // Use update endpoint to set isDefault to true
      const response = await fetch(`${getApiBaseUrl()}/users/addresses/${addressId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          isDefault: true
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ API Service: Default address set successfully');
        return { success: true, data: data.data, message: data.message || 'Default address set successfully' };
      } else {
        console.error('❌ API Service: Set default address failed:', data);
        throw new Error(data.message || data.error || 'Set default address failed');
      }
    } catch (error) {
      console.error('❌ API Service: Set default address error:', error);
      throw error;
    }
  },
};

/**
 * Public storefront catalog: include products from admin and team members.
 * Backend should return the full published catalog (do not filter to admin `createdBy` only).
 * String values keep query strings explicit for strict backends.
 * Ignored safely if the API does not read a param yet.
 */
const PUBLIC_PRODUCT_CATALOG_PARAMS = {
  includeAllCreators: 'true',
  includeTeamCreators: 'true',
};

const DISABLE_TEAM_PRODUCT_SUPPLEMENT =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.REACT_APP_DISABLE_TEAM_PRODUCT_SUPPLEMENT === 'true';

/**
 * Public product GET responses vary: { data }, { data: { product } }, { success, data }, nested data.data, etc.
 */
function unwrapProductFromApiResponse(response) {
  if (response == null || typeof response !== 'object') return response;
  let doc = response.data !== undefined ? response.data : response;
  for (let hop = 0; hop < 5; hop++) {
    if (!doc || typeof doc !== 'object') break;
    if (doc.product && typeof doc.product === 'object' && (doc.product._id || doc.product.images)) {
      doc = doc.product;
      continue;
    }
    if (doc.data?.product && typeof doc.data.product === 'object') {
      doc = doc.data.product;
      continue;
    }
    if (
      doc.success &&
      doc.data &&
      typeof doc.data === 'object' &&
      !doc.images &&
      (doc.data._id || doc.data.images || doc.data.name)
    ) {
      doc = doc.data;
      continue;
    }
    if (
      doc.data &&
      typeof doc.data === 'object' &&
      !doc._id &&
      !doc.images &&
      (doc.data._id || doc.data.images)
    ) {
      doc = doc.data;
      continue;
    }
    break;
  }
  return doc;
}

// Product Services
export const productService = {
  // ✅ UPDATED: Get all products (main API - 53 products)
  getProducts: async (filters = {}) => {
    const params = {
      ...PUBLIC_PRODUCT_CATALOG_PARAMS,
      page: filters.page || 1,
      limit: filters.limit || 12,
      sort: filters.sort || 'createdAt',
      order: filters.order || 'desc',
      category: filters.category,
      brand: filters.brand,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      gender: filters.gender,
      size: filters.size,
      color: filters.color,
      search: filters.search,
      material: filters.material,
      occasion: filters.occasion,
      season: filters.season,
      fit: filters.fit,
      inStock: filters.inStock,
      tag: filters.tag, // ✅ Tag filter for filtering products by tag
      createdBy: filters.createdBy,
    };

    if (DISABLE_TEAM_PRODUCT_SUPPLEMENT) {
      const response = await apiRequest('/products', { method: 'GET', params });
      const raw = response.data !== undefined ? response.data : response;
      const products = normalizeProductsArray(raw);
      return {
        success: true,
        products,
        pagination: response.pagination,
        message: response.message || 'Products loaded successfully',
      };
    }

    const [mainRes, teamRes] = await Promise.allSettled([
      apiRequest('/products', { method: 'GET', params }),
      apiRequest('/products', {
        method: 'GET',
        params: { ...params, teamOnly: 'true' },
      }),
    ]);

    if (mainRes.status === 'rejected') throw mainRes.reason;

    const response = mainRes.value;
    const raw = response.data !== undefined ? response.data : response;
    let products = normalizeProductsArray(raw);

    if (teamRes.status === 'fulfilled') {
      const teamEnvelope = teamRes.value;
      const teamRaw = teamEnvelope.data !== undefined ? teamEnvelope.data : teamEnvelope;
      const teamProducts = normalizeProductsArray(teamRaw);
      if (teamProducts.length) {
        products = mergeProductListsById(products, teamProducts);
      }
    }

    return {
      success: true,
      products,
      pagination: response.pagination,
      message: response.message || 'Products loaded successfully',
    };
  },

  // Get product by ID — unwrap nested API envelopes so images[].linkedColor is reachable
  getProduct: async (productId) => {
    const response = await apiRequest(`/products/${productId}`);
    const doc = unwrapProductFromApiResponse(response);
    return {
      success: true,
      product: doc,
      message: response.message || 'Product loaded successfully',
    };
  },

  // Get product by URL (SEO-friendly)
  getProductByUrl: async (productUrl) => {
    const response = await apiRequest(`/products/url/${productUrl}`);
    const doc = unwrapProductFromApiResponse(response);
    return {
      success: true,
      product: doc,
      message: response.message || 'Product loaded successfully',
    };
  },

  /**
   * Record a product page view (public). Backend increments server-side counter.
   * POST /products/:id/views — optional response body { views, viewCount }.
   */
  recordProductView: async (productId) => {
    if (!productId) return { success: false };
    try {
      const response = await apiRequest(`/products/${encodeURIComponent(productId)}/views`, {
        method: 'POST',
      });
      const payload = response?.data !== undefined ? response.data : response;
      const views =
        payload?.views ??
        payload?.viewCount ??
        payload?.count ??
        response?.views ??
        response?.viewCount;
      return {
        success: response?.success !== false,
        views: views != null ? Number(views) : undefined,
      };
    } catch {
      return { success: false };
    }
  },

  /**
   * Current view count for PDP badge (public).
   * GET /products/:id/views — expect { views } or { viewCount } or { data: { views } }.
   */
  getProductViewCount: async (productId) => {
    if (!productId) return { success: false, views: 1 };
    try {
      const response = await apiRequest(`/products/${encodeURIComponent(productId)}/views`, {
        method: 'GET',
      });
      const payload = response?.data !== undefined ? response.data : response;
      const raw =
        payload?.views ??
        payload?.viewCount ??
        payload?.count ??
        response?.views ??
        response?.viewCount ??
        1;
      const n = Number(raw);
      return { success: true, views: Number.isFinite(n) && n >= 1 ? n : 1 };
    } catch {
      return { success: false, views: 1 };
    }
  },

  // Get product creator (name, role) - optional; many backends omit GET /products/:id/creator
  // Use fetch here (not apiRequest) so 404/500 does not log "API Request Error" to the console.
  getProductCreator: async (productId) => {
    if (!productId) return { success: false };
    try {
      const url = `${apiConfig.baseURL}/products/${productId}/creator`;
      const response = await fetch(url, { headers: { ...getAuthHeaders() } });
      if (!response.ok) return { success: false };
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) return { success: false };
      const res = await response.json();
      const data = res?.data || res;
      const name = data?.name ?? res?.name ?? data?.user?.name;
      if (name) return { success: true, name: String(name), role: data?.role ?? res?.role ?? 'team' };
      return { success: false };
    } catch {
      return { success: false };
    }
  },

  // Get creator from admin API (same as admin products - has createdBy populated)
  // Use when admin token exists - admin products API returns createdBy with name
  getProductCreatorFromAdmin: async (productId) => {
    if (!productId || !localStorage.getItem('adminToken')) return { success: false };
    try {
      const res = await adminApiRequest(`/admin/products/${productId}`);
      const product = res?.data?.product ?? res?.product ?? res?.data ?? res;
      const createdBy = product?.createdBy;
      if (createdBy && typeof createdBy === 'object' && (createdBy.name || createdBy.email)) {
        return {
          success: true,
          name: createdBy.name || createdBy.email,
          role: createdBy.role || 'team'
        };
      }
      return { success: false };
    } catch {
      return { success: false };
    }
  },

  // Search products (uses same catalog merge as getProducts)
  searchProducts: async (query) => {
    return productService.getProducts({ search: query });
  },

  // ✅ UPDATED: Get products by category (from main 53 products)
  getProductsByCategory: async (category, page = 1, limit = 100) => {
    return productService.getProducts({ category, page, limit });
  },

  /** Legacy alias — homepage Featured carousel (`isFeatured` products). */
  getFeaturedProducts: async (limit = 8) => {
    return productService.getHomeFeaturedProducts(limit);
  },

  /**
   * Homepage Featured — products with admin `isFeatured: true` (public catalog).
   */
  getHomeFeaturedProducts: async (limit = 8) => {
    const cap = Math.min(Math.max(Number(limit) || 8, 1), 24);
    const queryAttempts = [
      { path: '/products/featured', params: { limit: cap } },
      {
        path: '/products',
        params: { limit: cap, isFeatured: 'true', featured: 'true' },
      },
      { path: '/products', params: { limit: cap, tag: 'featured' } },
    ];

    for (const { path, params } of queryAttempts) {
      try {
        const response = await apiRequest(path, {
          method: 'GET',
          params,
          silentLog: true,
        });
        if (response?.success === false) continue;
        const { products } = parseTrendingByViewsResponse(response);
        const rows = normalizeProductsArray(
          products.length ? products : response?.products ?? response
        );
        if (rows.length && rows.some((p) => p?.name)) {
          return { success: true, products: rows, source: path };
        }
      } catch {
        /* next attempt */
      }
    }

    try {
      const res = await productService.getProducts({
        limit: Math.max(cap, 100),
        sort: 'createdAt',
        order: 'desc',
      });
      const products = Array.isArray(res.products) ? res.products : [];
      return {
        success: products.length > 0,
        products,
        source: 'catalog-filter-isFeatured',
      };
    } catch {
      return { success: false, products: [], source: null };
    }
  },

  // ✅ UPDATED: Get new arrivals (first 8 from main 53 products)
  getNewArrivals: async () => {
    return productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
  },

  // ✅ UPDATED: Get sale products (first 8 from main 53 products)
  getSaleProducts: async () => {
    return productService.getProducts({ limit: 8, sort: 'createdAt', order: 'desc' });
  },

  /**
   * Homepage Trending — top products by PDP view count (same store as GET/POST /products/:id/views).
   * Tries dedicated public routes first, then catalog sort=viewCount.
   */
  getTrendingByPdpViews: async (limit = 8) => {
    const cap = Math.min(Math.max(Number(limit) || 8, 1), 24);
    const candidates = [
      { path: '/products/trending-by-views', params: { limit: cap } },
      { path: '/products/most-viewed', params: { limit: cap } },
    ];

    for (const { path, params } of candidates) {
      try {
        const response = await apiRequest(path, {
          method: 'GET',
          params,
          silentLog: true,
        });
        if (response?.success === false) continue;

        const { products, topProducts } = parseTrendingByViewsResponse(response);
        if (products.length && products.some((p) => p?.name)) {
          return { success: true, products, topProducts, source: path };
        }
        if (topProducts.length) {
          return { success: true, products: [], topProducts, source: path };
        }
      } catch {
        /* try next route or catalog sort */
      }
    }

    for (const sortField of ['viewCount', 'views']) {
      try {
        const res = await productService.getProducts({
          limit: cap,
          sort: sortField,
          order: 'desc',
        });
        const products = Array.isArray(res.products) ? res.products : [];
        if (products.length) {
          return {
            success: true,
            products,
            topProducts: [],
            source: `catalog-sort-${sortField}`,
          };
        }
      } catch {
        /* try next sort field */
      }
    }

    return { success: false, products: [], topProducts: [], source: null };
  },
};

const CREATOR_PRODUCTS_PAGE_LIMIT = 100;

const creatorPublicProfileCache = new Map();
const creatorPublicProfileInflight = new Map();
const CREATOR_PUBLIC_PROFILE_CACHE_MS = 60_000;

function creatorPublicProfileCacheKey(identifier) {
  return String(identifier || '').trim().toLowerCase();
}

function rememberCreatorPublicProfileCache(identifier, result) {
  const key = creatorPublicProfileCacheKey(identifier);
  if (!key) return;
  creatorPublicProfileCache.set(key, { at: Date.now(), value: result });
  const c = result?.creator;
  if (c?._id) {
    creatorPublicProfileCache.set(creatorPublicProfileCacheKey(c._id), {
      at: Date.now(),
      value: result,
    });
  }
  const slug = normalizeCreatorSlugInput(c?.creatorSlug);
  if (slug) {
    creatorPublicProfileCache.set(creatorPublicProfileCacheKey(slug), {
      at: Date.now(),
      value: result,
    });
  }
}

/** Public creator storefront — profile + products by team member / admin. */
export const creatorStorefrontService = {
  /** GET /creators/:identifier/public — MongoDB id or shop handle (slug). */
  getCreatorProfile: async (identifier) => {
    if (!identifier) return { success: false, creator: null };

    const cacheKey = creatorPublicProfileCacheKey(identifier);
    const cached = creatorPublicProfileCache.get(cacheKey);
    if (cached && Date.now() - cached.at < CREATOR_PUBLIC_PROFILE_CACHE_MS) {
      return cached.value;
    }
    if (creatorPublicProfileInflight.has(cacheKey)) {
      return creatorPublicProfileInflight.get(cacheKey);
    }

    const work = (async () => {
      try {
        const response = await apiRequest(`/creators/${encodeURIComponent(identifier)}/public`, {
          method: 'GET',
        });
        let data =
          response?.data?.creator ??
          response?.data?.data?.creator ??
          response?.creator ??
          response?.data ??
          response;
        if (data?.creator && typeof data.creator === 'object') {
          data = data.creator;
        }
        if (data && typeof data === 'object' && (data._id || data.id || data.name)) {
          const id = data._id || data.id;
          const slug = normalizeCreatorSlugInput(data.creatorSlug);
          if (id && slug) rememberCreatorSlug(id, slug);
          const result = { success: true, creator: data };
          rememberCreatorPublicProfileCache(identifier, result);
          return result;
        }
        const fail = { success: false, creator: null };
        rememberCreatorPublicProfileCache(identifier, fail);
        return fail;
      } catch {
        return { success: false, creator: null };
      }
    })();

    creatorPublicProfileInflight.set(cacheKey, work);
    try {
      return await work;
    } finally {
      creatorPublicProfileInflight.delete(cacheKey);
    }
  },

  getProductsByCreator: async (identifier, options = {}) => {
    if (!identifier) return { success: false, products: [] };
    const page = options.page || 1;
    const limit = options.limit || CREATOR_PRODUCTS_PAGE_LIMIT;
    return productService.getProducts({
      createdBy: identifier,
      page,
      limit,
      sort: options.sort || 'createdAt',
      order: options.order || 'desc',
    });
  },

  /** Paginate public catalog filtered by creator id or slug (API param + client fallback). */
  fetchAllProductsByCreator: async (identifier) => {
    if (!identifier) return [];

    const profileRes = await creatorStorefrontService.getCreatorProfile(identifier);
    const canonicalCreatorId =
      profileRes?.creator?._id ||
      profileRes?.creator?.id ||
      (isMongoObjectIdString(identifier) ? String(identifier) : '');

    const productMatches = (p) =>
      creatorProductMatchesIdentifier(p?.createdBy, identifier, canonicalCreatorId);

    const merged = [];
    let page = 1;
    const maxPages = 50;

    while (page <= maxPages) {
      const res = await creatorStorefrontService.getProductsByCreator(identifier, {
        page,
        limit: CREATOR_PRODUCTS_PAGE_LIMIT,
      });
      const batch = normalizeProductsArray(res?.products ?? res?.data ?? res);
      if (batch.length) merged.push(...batch);
      if (batch.length < CREATOR_PRODUCTS_PAGE_LIMIT) break;
      page += 1;
    }

    let filtered = merged.filter(productMatches);

    if (filtered.length > 0) return filtered;

    page = 1;
    const all = [];
    while (page <= maxPages) {
      const res = await productService.getProducts({
        page,
        limit: CREATOR_PRODUCTS_PAGE_LIMIT,
        sort: 'createdAt',
        order: 'desc',
      });
      const batch = normalizeProductsArray(res?.products ?? res?.data ?? res);
      if (batch.length) all.push(...batch);
      if (batch.length < CREATOR_PRODUCTS_PAGE_LIMIT) break;
      page += 1;
    }

    filtered = all.filter(productMatches);

    return filtered;
  },
};

// Re-open productService export block for admin methods — attach below on productService object
Object.assign(productService, {
  // ✅ UPDATED: Admin methods for product management (all 53 products are main products)
  // Get all products for admin (from main 53 products)
  getAdminProducts: async (params = {}) => {
    try {
      console.log('🔧 Admin products API call...');
      console.log('🔑 Admin token:', localStorage.getItem('adminToken') ? 'Present' : 'Missing');
      
      const response = await adminApiRequest('/products', {
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 1000, // High limit to get all 53 products
          sort: params.sort || 'createdAt',
          order: params.order || 'desc',
          search: params.search,
          category: params.category,
          status: params.status
        }
      });
      
      console.log('✅ Admin products API response:', response);
      
      // Return products array directly as specified in backend response format
      return { 
        success: true, 
        products: response.data || response, 
        message: response.message || 'Admin products loaded successfully' 
      };
    } catch (error) {
      console.error('❌ Admin products API error:', error);
      throw error;
    }
  },

  addProduct: async (productData) => {
    return adminApiRequest('/admin/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },

  // Get Product for Editing (Admin)
  // API: GET /api/admin/products/:id
  // Response: { success: true, message: "Product successfully loaded", data: { product: {...} } }
  getProductForEdit: async (productId) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    console.log('📥 GET Product for Edit - Product ID:', productId);
    console.log('📥 GET Product for Edit - Endpoint: GET /api/admin/products/' + productId);
    
    try {
      const response = await adminApiRequest(`/admin/products/${productId}`, {
        method: 'GET',
      });
      
      console.log('📥 GET Product for Edit - Response:', response);
      
      // Return product data in consistent format
      if (response.success && response.data && response.data.product) {
        return {
          success: true,
          product: response.data.product,
          message: response.message || 'Product successfully loaded'
        };
      }
      
      // Fallback if response structure is different
      return {
        success: true,
        product: response.data || response,
        message: response.message || 'Product successfully loaded'
      };
    } catch (error) {
      console.error('❌ GET Product for Edit - Error:', error);
      if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
        throw new Error(`Product endpoint not found. Please verify backend route: GET /api/admin/products/:id`);
      }
      throw error;
    }
  },

  /**
   * Optional product showcase video (max 5MB on client). Multipart POST — do not set Content-Type manually.
   * Backend: see docs/BACKEND_PROMPT_PRODUCT_VIDEO.md
   */
  uploadProductVideo: async (productId, file) => {
    if (!productId || !file) {
      throw new Error('Product ID and video file are required');
    }
    const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Admin authentication required');
    }
    const formData = new FormData();
    formData.append('productVideo', file);
    const url = getApiUrl(`/admin/products/${encodeURIComponent(productId)}/video`);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    if (!response.ok) {
      const msg =
        typeof data === 'object' && data && (data.message || data.error)
          ? data.message || data.error
          : `HTTP ${response.status}`;
      throw new Error(typeof msg === 'string' ? msg : 'Video upload failed');
    }
    return data;
  },

  // Update Product (Admin)
  // API: PUT /api/admin/products/:id
  // Body: { name, description, price, ... } (all fields optional)
  // Response: { success: true, message: "Product update ho gaya hai", data: { product: {...} } }
  updateProduct: async (productId, productData) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    console.log('📤 PUT Update Product - Product ID:', productId);
    console.log('📤 PUT Update Product - Endpoint: PUT /api/admin/products/' + productId);
    console.log('📤 PUT Update Product - Request body:', productData);
    
    try {
      const response = await adminApiRequest(`/admin/products/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(productData),
      });
      
      console.log('📥 PUT Update Product - Response:', response);
      
      if (response.success === false) {
        console.error('❌ PUT Update Product - API returned error:', response);
        if (response.errors && Array.isArray(response.errors)) {
          const errorMessages = response.errors
            .map((e) => `${e.param || e.field || 'field'}: ${e.msg || e.message || e}`)
            .join(', ');
          throw new Error(
            JSON.stringify({ error: errorMessages, errors: response.errors, status: response.status || 400 })
          );
        }
        const errMsg = response.message || response.error || 'Product update failed';
        throw new Error(JSON.stringify({ error: errMsg, status: response.status || 400 }));
      }

      const product =
        response.data?.product ||
        response.product ||
        (response.data && typeof response.data === 'object' && !Array.isArray(response.data)
          ? response.data
          : null);

      return {
        success: true,
        product: product || response,
        message: response.message || 'Product update ho gaya hai',
      };
    } catch (error) {
      console.error('❌ PUT Update Product - Error:', error);
      console.error('❌ PUT Update Product - Error message:', error.message);
      console.error('❌ PUT Update Product - Error stack:', error.stack);
      
      if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
        throw new Error(`Product update endpoint not found. Please verify backend route: PUT /api/admin/products/:id`);
      }
      throw error;
    }
  },

  deleteProduct: async (productId) => {
    return adminApiRequest(`/admin/products/${productId}`, {
      method: 'DELETE',
    });
  },

  clearAllProducts: async () => {
    return apiRequest('/admin/products/clear-all', {
      method: 'DELETE',
    });
  },

  // Stock Management APIs
  // API 1: Single Stock Update (Recommended for popup windows)
  // Endpoint: PUT /api/admin/products/stock
  // Body: { productId, color, size, quantity }
  // Response: { success: true, message: "Stock updated successfully", data: { product: {...}, updatedStock: {...} } }
  updateStock: async (productId, color, size, quantity) => {
    // Validate all required fields
    if (!productId) {
      throw new Error('Product ID is required');
    }
    if (!color || !color.trim()) {
      throw new Error('Color is required');
    }
    if (!size || !size.trim()) {
      throw new Error('Size is required');
    }

    // Ensure quantity is an integer
    const quantityInt = parseInt(quantity, 10);
    if (isNaN(quantityInt) || quantityInt < 0) {
      throw new Error('Quantity must be a non-negative integer');
    }

    console.log('📤 PUT Stock Update - Request:', {
      productId: String(productId).trim(),
      color: String(color).trim(),
      size: formatSizeForStockApi(size),
      quantity: quantityInt
    });
    console.log('📤 PUT Stock Update - Endpoint: PUT /api/admin/products/stock');

    try {
      const response = await adminApiRequest('/admin/products/stock', {
        method: 'PUT',
        body: JSON.stringify({
          productId: String(productId).trim(),
          color: String(color).trim(),
          size: formatSizeForStockApi(size),
          quantity: quantityInt
        }),
      });
      
      console.log('📥 PUT Stock Update - Response:', response);
      return response;
    } catch (error) {
      console.error('❌ PUT Stock Update - Error:', error);
      // Check if it's a 404 error
      if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
        throw new Error(`Stock update endpoint not found. Please verify backend route: PUT /api/admin/products/stock`);
      }
      throw error;
    }
  },

  // API 2: Stock Update by Product ID (Alternate Route)
  // Endpoint: PUT /api/admin/products/:id/stock
  // Body: { color, size, quantity }
  updateStockById: async (productId, color, size, quantity) => {
    // Ensure quantity is an integer
    const quantityInt = parseInt(quantity, 10);
    if (isNaN(quantityInt) || quantityInt < 0) {
      throw new Error('Quantity must be a non-negative integer');
    }

    const response = await adminApiRequest(`/admin/products/${productId}/stock`, {
      method: 'PUT',
      body: JSON.stringify({
        color: String(color).trim(),
        size: formatSizeForStockApi(size),
        quantity: quantityInt
      }),
    });
    return response;
  },

  // API 3: Bulk Stock Update (For multiple color-size combinations)
  // Endpoint: PUT /api/admin/products/stock/bulk
  // Body: { productId, stockUpdates: [{ color, size, quantity }, ...] }
  bulkUpdateStock: async (productId, stockUpdates) => {
    // Validate and format stock updates
    const formattedUpdates = stockUpdates.map(update => {
      const color = stockColorKeyForApi(update.color);
      const size = formatSizeForStockApi(update.size);
      const quantity = parseInt(update.quantity, 10);

      // Validate all fields
      if (!color) {
        throw new Error('Color is required for all stock updates');
      }
      if (!size) {
        throw new Error('Size is required for all stock updates');
      }
      if (isNaN(quantity) || quantity < 0) {
        throw new Error(`Invalid quantity for ${color} - ${size}: ${update.quantity}`);
      }

      return {
        color,
        size,
        quantity
      };
    });

    const response = await adminApiRequest('/admin/products/stock/bulk', {
      method: 'PUT',
      body: JSON.stringify({
        productId: String(productId).trim(),
        stockUpdates: formattedUpdates
      }),
    });
    return response;
  },

  // Get Stock Details (Admin)
  // API: GET /api/admin/products/stock/:id
  // Response: { success: true, data: { product: { id, name, stock, colors, sizes } } }
  getStockDetails: async (productId) => {
    // Validate productId
    if (!productId) {
      throw new Error('Product ID is required');
    }

    console.log('📥 GET Stock Details - Product ID:', productId);
    console.log('📥 GET Stock Details - Endpoint: GET /api/admin/products/stock/' + productId);

    try {
      const response = await adminApiRequest(`/admin/products/stock/${productId}`, {
        method: 'GET',
      });
      
      console.log('📥 GET Stock Details - Response:', response);
      return response;
    } catch (error) {
      console.error('❌ GET Stock Details - Error:', error);
      // Check if it's a 404 error
      if ((error.message && error.message.includes('404')) || (error.message && error.message.includes('not found'))) {
        throw new Error(`Stock endpoint not found. Please verify backend route: GET /api/admin/products/stock/:id`);
      }
      throw error;
    }
  },

  // Get Product Colors (Public)
  // API: GET /api/products/:id/colors
  // Response: { success: true, data: { colors: [...] } }
  getProductColors: async (productId) => {
    const response = await apiRequest(`/products/${productId}/colors`, {
      method: 'GET',
    });
    return response;
  },

  // Get Product Sizes by Color (Public)
  // API: GET /api/products/:id/sizes?color=Black
  // Response: { success: true, data: { sizes: [...], stock: {...} } }
  getProductSizes: async (productId, color) => {
    const response = await apiRequest(`/products/${productId}/sizes`, {
      method: 'GET',
      params: { color }
    });
    return response;
  },

  // Check Stock Availability (Public)
  // API: GET /api/products/:id/stock/check?color=Black&size=M
  // Response: { success: true, data: { stock: 10, available: true } }
  checkStockAvailability: async (productId, color, size = '') => {
    const params = { color };
    if (size) {
      params.size = size;
    }
    const response = await apiRequest(`/products/${productId}/stock/check`, {
      method: 'GET',
      params
    });
    return response;
  },

  // Legacy method for backward compatibility
  updateProductStock: async (productId, stockData) => {
    return adminApiRequest(`/admin/products/${productId}/stock`, {
      method: 'PUT',
      body: JSON.stringify(stockData),
    });
  },
});

// Cart Services
// All endpoints match the backend API specification:
// GET /api/cart - Get user cart
// POST /api/cart/items - Add item to cart
// PUT /api/cart/items/:itemId - Update item quantity
// DELETE /api/cart/items/:itemId - Remove item from cart
// DELETE /api/cart - Clear entire cart
// POST /api/cart/coupon - Apply coupon
// DELETE /api/cart/coupon - Remove coupon

/** Collapse concurrent GET /cart (e.g. Header + Checkout) into one network round-trip. Retries bypass this. */
let getCartInFlight = null;

async function getCartRequest(retryCount = 0) {
  const maxRetries = 2;
  const emptyCartData = { items: [], subtotal: 0, total: 0 };

  try {
    const token = getAuthToken();
    const url = `${apiConfig.baseURL}/cart`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json',
      },
    });

    const contentType = res.headers.get('content-type');
    let payload;
    if (contentType && contentType.includes('application/json')) {
      payload = await res.json();
    } else {
      payload = await res.text();
    }

    if (res.status === 401) {
      setAuthToken(null);
      if (!authExpiredDispatched) {
        authExpiredDispatched = true;
        window.dispatchEvent(new CustomEvent('authExpired'));
        if (authExpiredTimeout) clearTimeout(authExpiredTimeout);
        authExpiredTimeout = setTimeout(() => {
          authExpiredDispatched = false;
        }, 5000);
      }
      const errorMessage =
        extractSheenBackendErrorMessageLoose(payload, 401) ||
        'Token expired. Please login again.';
      throw new Error(JSON.stringify({ error: errorMessage }));
    }

    if (!res.ok) {
      const errorMessage =
        extractSheenBackendErrorMessageLoose(payload, res.status) ||
        (typeof payload === 'string' ? payload : 'Cart unavailable');

      const isVersionConflict =
        errorMessage.includes('No matching document found') ||
        errorMessage.includes('version') ||
        errorMessage.includes('modifiedPaths');

      if (isVersionConflict && retryCount < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 300 * (retryCount + 1)));
        return getCartRequest(retryCount + 1);
      }

      console.warn('GET /api/cart failed:', res.status, errorMessage);
      return {
        success: false,
        data: emptyCartData,
        error: errorMessage,
      };
    }

    return payload;
  } catch (error) {
    const msg = String(error?.message || '');
    if (msg.startsWith('{"error"')) {
      throw error;
    }

    let errorMessage = msg;
    try {
      if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
        const parsed = JSON.parse(errorMessage);
        errorMessage = parsed.error || parsed.message || errorMessage;
      }
    } catch {
      /* use msg */
    }

    const isVersionConflict =
      errorMessage.includes('No matching document found') ||
      errorMessage.includes('version') ||
      errorMessage.includes('modifiedPaths');

    if (isVersionConflict && retryCount < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 300 * (retryCount + 1)));
      return getCartRequest(retryCount + 1);
    }

    console.warn('cartService.getCart:', errorMessage || error);
    return {
      success: false,
      data: emptyCartData,
      error: errorMessage || 'Network error',
    };
  }
}

export const cartService = {
  /** Contract: docs/BACKEND_STOREFRONT_API_REFERENCE.md (cart section). */
  /**
   * GET /api/cart
   * Get user cart
   * Returns: { success: true, data: { _id, user, items: [...], subtotal, total, coupon, ... } }
   *
   * Uses fetch directly (not apiRequest) so a buggy backend 500 does not spam console.error;
   * returns { success: false, data: empty shell } so UI can show an empty bag instead of mixing guest cart.
   * Concurrent callers at retryCount 0 share one in-flight request.
   */
  getCart: async (retryCount = 0) => {
    if (retryCount > 0) {
      return getCartRequest(retryCount);
    }
    if (getCartInFlight) {
      return getCartInFlight;
    }
    getCartInFlight = getCartRequest(0).finally(() => {
      getCartInFlight = null;
    });
    return getCartInFlight;
  },

  /**
   * POST /api/cart/items
   * Add item to cart
   * Body: { productId, quantity, color, size }
   * Returns: { success: true, message: "Item added to cart successfully", data: { ... } }
   */
  addToCart: async (productId, quantity, color, size) => {
    const pid = productId != null && productId !== '' ? String(productId) : productId;
    return apiRequest('/cart/items', {
      method: 'POST',
      body: JSON.stringify({
        productId: pid,
        quantity,
        color: stockColorKeyForApi(color ?? 'default'),
        size: formatSizeForStockApi(size ?? 'default'),
      }),
      silentLog: true,
    });
  },

  /**
   * DELETE /api/cart/coupon — fetch only, no throw (logout / clear flows).
   * Helps backends that 400 on DELETE /cart when `coupon.type` is null until DB is fixed.
   */
  removeCouponQuiet: async () => {
    try {
      const token = getAuthToken();
      if (!token) return { success: false };
      const res = await fetch(`${apiConfig.baseURL}/cart/coupon`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      return { success: res.ok, status: res.status };
    } catch {
      return { success: false };
    }
  },

  /**
   * DELETE /api/cart — best-effort clear (e.g. before logout). Does not throw; avoids noisy console on backend bugs.
   * Strips coupon first (quiet) so servers that validate coupon on clear are less likely to return 400.
   */
  clearCartQuiet: async () => {
    try {
      const token = getAuthToken();
      if (!token) return { success: false };
      await cartService.removeCouponQuiet();
      const url = `${apiConfig.baseURL}/cart`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (!res.ok) return { success: false, status: res.status };
      const ct = res.headers.get('content-type');
      if (ct && ct.includes('application/json')) {
        return await res.json();
      }
      return { success: true };
    } catch {
      return { success: false };
    }
  },

  /**
   * PUT /api/cart/items/:itemId
   * Update cart item quantity
   * Body: { quantity }
   * Returns: { success: true, message: "Cart item updated successfully", data: { ... } }
   */
  updateCartItem: async (itemId, quantity, retryCount = 0) => {
    const maxRetries = 2;
    try {
      return await apiRequest(`/cart/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ quantity }),
      });
    } catch (error) {
      // Parse error message (might be JSON string)
      let errorMessage = error?.message || error?.error || '';
      try {
        if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || parsed.message || errorMessage;
        }
      } catch (e) {
        // Not JSON, use as is
      }
      
      // Check if it's a version conflict error
      const isVersionConflict = errorMessage.includes('No matching document found') || 
                                errorMessage.includes('version') ||
                                errorMessage.includes('modifiedPaths');
      
      if (isVersionConflict && retryCount < maxRetries) {
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
        // Retry the operation
        return cartService.updateCartItem(itemId, quantity, retryCount + 1);
      }
      throw error;
    }
  },

  /**
   * DELETE /api/cart/items/:itemId
   * Remove item from cart
   * Returns: { success: true, message: "Item removed from cart successfully", data: { ... } }
   */
  removeFromCart: async (itemId, retryCount = 0) => {
    const maxRetries = 2;
    try {
      return await apiRequest(`/cart/items/${itemId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      // Parse error message (might be JSON string)
      let errorMessage = error?.message || error?.error || '';
      try {
        if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || parsed.message || errorMessage;
        }
      } catch (e) {
        // Not JSON, use as is
      }
      
      // Check if it's a version conflict error
      const isVersionConflict = errorMessage.includes('No matching document found') || 
                                errorMessage.includes('version') ||
                                errorMessage.includes('modifiedPaths');
      
      if (isVersionConflict && retryCount < maxRetries) {
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
        // Retry the operation
        return cartService.removeFromCart(itemId, retryCount + 1);
      }
      throw error;
    }
  },

  /**
   * DELETE /api/cart
   * Clear entire cart
   * Returns: { success: true, message: "Cart cleared successfully", data: { ... } }
   */
  clearCart: async (retryCount = 0) => {
    const maxRetries = 2;
    try {
      return await apiRequest('/cart', {
        method: 'DELETE',
      });
    } catch (error) {
      // Parse error message (might be JSON string)
      let errorMessage = error?.message || error?.error || '';
      try {
        if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || parsed.message || errorMessage;
        }
      } catch (e) {
        // Not JSON, use as is
      }
      
      // Check if it's a version conflict error
      const isVersionConflict = errorMessage.includes('No matching document found') || 
                                errorMessage.includes('version') ||
                                errorMessage.includes('modifiedPaths');
      
      if (isVersionConflict && retryCount < maxRetries) {
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
        // Retry the operation
        return cartService.clearCart(retryCount + 1);
      }
      throw error;
    }
  },

  /**
   * POST /api/cart/coupon
   * Apply coupon to cart
   * Body: { code }
   * Returns: { success: true, message: "Coupon applied successfully", data: { ... } }
   */
  applyCoupon: async (code) => {
    return apiRequest('/cart/coupon', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  /**
   * DELETE /api/cart/coupon
   * Remove coupon from cart
   * Returns: { success: true, message: "Coupon removed successfully", data: { ... } }
   */
  removeCoupon: async (cartId = null) => {
    try {
      return await apiRequest('/cart/coupon', {
        method: 'DELETE',
      });
    } catch (error) {
      let msg = error?.message || '';
      try {
        if (typeof msg === 'string' && msg.startsWith('{')) {
          const parsed = JSON.parse(msg);
          msg = parsed?.error || parsed?.message || msg;
        }
      } catch {
        // keep original message
      }

      // Backend compatibility fallback:
      // Some builds fail DELETE /cart/coupon with:
      // "`null` is not a valid enum value for path `coupon.type`."
      // In this case, verify latest cart state before failing.
      const isCouponTypeEnumNullError =
        String(msg).toLowerCase().includes('coupon.type') &&
        String(msg).toLowerCase().includes('null') &&
        String(msg).toLowerCase().includes('enum');

      if (isCouponTypeEnumNullError) {
        try {
          const latest = await cartService.getCart();
          const cartData = latest?.data || latest;
          const stillHasCoupon = Boolean(
            cartData?.coupon &&
            (cartData.coupon.code || cartData.coupon.type || cartData.coupon._id)
          );
          if (!stillHasCoupon) {
            return {
              success: true,
              data: cartData,
              message: 'Coupon removed successfully',
            };
          }
          // Frontend-safe fallback:
          // Backend rejected removal due to enum null validation,
          // but user explicitly requested coupon removal. Keep UI/cart flow usable.
          const items = Array.isArray(cartData?.items) ? cartData.items : [];
          const itemsSubtotal = items.reduce((sum, item) => {
            const price = Number(item?.price) || 0;
            const qty = Number(item?.quantity) || 1;
            return sum + (price * qty);
          }, 0);
          return {
            success: true,
            data: {
              ...(cartData || {}),
              coupon: null,
              subtotal: itemsSubtotal,
              total: itemsSubtotal,
            },
            message: 'Coupon removed locally',
            localOnly: true,
          };
        } catch {
          // Ignore refresh-check errors and fall through to original error.
        }
      }

      throw error;
    }
  },
};

/**
 * Customer wishlist — full contract: docs/BACKEND_STOREFRONT_API_REFERENCE.md
 * GET /api/wishlist — typically `{ success, data: { items: [...] } }` (DTOs + product shape).
 */
export const wishlistService = {
  getWishlist: async () => apiRequest('/wishlist'),
  getWishlistSummary: async () => apiRequest('/wishlist/summary'),
  getWishlistItems: async () => apiRequest('/wishlist/items'),
  checkInWishlist: async (productId) =>
    apiRequest(`/wishlist/check/${encodeURIComponent(String(productId))}`, {
      method: 'GET',
      silentLog: true,
    }),
  addToWishlist: async (productId) =>
    apiRequest('/wishlist', {
      method: 'POST',
      body: JSON.stringify({
        productId: productId != null && productId !== '' ? String(productId) : productId,
      }),
    }),
  /** POST /wishlist/items — if already liked, backend may toggle = remove. */
  addWishlistItemOrToggle: async (productId, note) =>
    apiRequest('/wishlist/items', {
      method: 'POST',
      body: JSON.stringify({
        product: String(productId),
        ...(note != null && note !== '' ? { note } : {}),
      }),
    }),
  removeWishlistItemLine: async (productId) =>
    apiRequest(`/wishlist/items/${encodeURIComponent(String(productId))}`, {
      method: 'DELETE',
    }),
  removeFromWishlist: async (productId) =>
    apiRequest(`/wishlist/${encodeURIComponent(String(productId))}`, {
      method: 'DELETE',
    }),
  clearWishlist: async () =>
    apiRequest('/wishlist', {
      method: 'DELETE',
    }),
  moveWishlistToCart: async (productId, payload = {}) =>
    apiRequest(`/wishlist/move-to-cart/${encodeURIComponent(String(productId))}`, {
      method: 'POST',
      body: JSON.stringify(payload && typeof payload === 'object' ? payload : {}),
    }),
};

/**
 * Authenticated user recent product views.
 * Contract: docs/BACKEND_STOREFRONT_API_REFERENCE.md — try `/me/...` then `/users/me/...`.
 */
export const recentVisitsService = {
  recordVisit: async (productId, visitedAt) => {
    const body = JSON.stringify({
      productId: String(productId),
      ...(visitedAt ? { visitedAt } : {}),
    });
    try {
      return await apiRequest('/me/recent-visits', { method: 'POST', body });
    } catch (e1) {
      try {
        return await apiRequest('/users/me/recent-visits', { method: 'POST', body });
      } catch (e2) {
        throw e2;
      }
    }
  },

  getRecentVisits: async () => {
    try {
      return await apiRequest('/me/recent-visits', { method: 'GET', silentLog: true });
    } catch {
      try {
        return await apiRequest('/users/me/recent-visits', { method: 'GET', silentLog: true });
      } catch {
        return { success: false, data: { visits: [] } };
      }
    }
  },

  clearRecentVisits: async () => {
    try {
      return await apiRequest('/me/recent-visits', { method: 'DELETE' });
    } catch {
      return await apiRequest('/users/me/recent-visits', { method: 'DELETE' });
    }
  },
};

// Order Services
export const orderService = {
  // Get user orders
  getUserOrders: async () => {
    return apiRequest('/orders');
  },

  /** Public: canonical payment options for checkout (cod | card | bank_transfer | digital_wallet). */
  getCheckoutPaymentMethods: async () => {
    try {
      const res = await apiRequest('/orders/checkout/payment-methods', {
        method: 'GET',
        silentLog: true,
      });
      const inner = res?.data != null && typeof res.data === 'object' ? res.data : res;
      const methods =
        inner?.methods ??
        (Array.isArray(inner) ? inner : null) ??
        (Array.isArray(res?.methods) ? res.methods : []) ??
        [];
      return {
        success: res?.success !== false,
        data: {
          methods: Array.isArray(methods) ? methods : [],
          message:
            (typeof inner?.message === 'string' && inner.message) ||
            (typeof res?.message === 'string' && res.message) ||
            '',
        },
      };
    } catch (e) {
      let parsed = null;
      try {
        parsed = JSON.parse(e.message);
      } catch {
        /* ignore */
      }
      return {
        success: false,
        data: { methods: [], message: '' },
        error: parsed?.error || e.message || 'Failed to load payment options',
      };
    }
  },

  // Get single order
  getOrder: async (orderId) => {
    return apiRequest(`/orders/${orderId}`);
  },

  // Create new order
  // ✅ DO NOT include orderNumber in orderData - backend automatically generates it
  createOrder: async (orderData) => {
    try {
      // Ensure orderNumber is not accidentally included
      const { orderNumber, ...cleanOrderData } = orderData;
      
      if (orderNumber) {
        console.warn('orderNumber was included in request but will be ignored - backend generates it automatically');
      }
      
      const response = await apiRequest('/orders', {
        method: 'POST',
        body: JSON.stringify(cleanOrderData),
      });
      
      // Verify orderNumber is in response
      if (response.success && response.data && !response.data.orderNumber) {
        console.warn('Order created but orderNumber not in response');
      }
      
      return response;
    } catch (error) {
      console.error('Order creation error:', error);

      let parsed = null;
      try {
        parsed = JSON.parse(error.message);
      } catch {
        parsed = null;
      }
      const fromParsed =
        parsed && typeof parsed === 'object'
          ? parsed
          : { error: error.message || 'Failed to place order' };

      // Handle validation errors
      if (error.errors && Array.isArray(error.errors)) {
        return {
          success: false,
          error: fromParsed.error || error.message || 'Validation failed',
          code: fromParsed.code,
          availablePaymentMethods: fromParsed.availablePaymentMethods,
          errors: error.errors,
        };
      }

      return {
        success: false,
        error: fromParsed.error || error.message || 'Failed to place order',
        code: fromParsed.code,
        availablePaymentMethods: fromParsed.availablePaymentMethods,
        errors: error.errors || [],
      };
    }
  },

  // Cancel order
  cancelOrder: async (orderId) => {
    return apiRequest(`/orders/${orderId}/cancel`, {
      method: 'PUT',
    });
  },

  // Update order status
  updateOrderStatus: async (orderId, status) => {
    return apiRequest(`/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  // Track order
  trackOrder: async (orderId) => {
    return apiRequest(`/orders/${orderId}/track`);
  },
};

// Account Services (Payment Accounts)
export const accountService = {
  // Get all payment accounts (Admin)
  getAccounts: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    return adminApiRequest(`/accounts${queryParams ? `?${queryParams}` : ''}`);
  },

  // Get visible payment accounts (Public) — base URL already includes /api
  getVisibleAccounts: async () => {
    const primary = await apiRequest('/accounts/visible');
    if (accountsArrayFromResponse(primary).length > 0) {
      return primary;
    }
    try {
      const alt = await apiRequest('/payments/methods/public');
      if (accountsArrayFromResponse(alt).length > 0) {
        return alt;
      }
    } catch (e) {
      console.warn('getVisibleAccounts: /payments/methods/public fallback failed', e);
    }
    return primary;
  },

  // Get single payment account
  getAccount: async (accountId) => {
    return adminApiRequest(`/accounts/${accountId}`);
  },

  // Create payment account
  createAccount: async (accountData) => {
    const payload = normalizeAccountPayloadForApi(accountData);
    return adminApiRequest('/accounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Update payment account
  updateAccount: async (accountId, accountData) => {
    const payload = normalizeAccountPayloadForApi(accountData);
    return adminApiRequest(`/accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  // Delete payment account
  deleteAccount: async (accountId) => {
    return adminApiRequest(`/accounts/${accountId}`, {
      method: 'DELETE',
    });
  },

  // Toggle account visibility
  toggleVisibility: async (accountId, isVisible) => {
    return adminApiRequest(`/accounts/${accountId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ isVisible }),
    });
  },

  // Toggle account active status
  toggleStatus: async (accountId, isActive) => {
    return adminApiRequest(`/accounts/${accountId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  },
};

// Revenue Services
export const revenueService = {
  // Get revenue overview
  getOverview: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return adminApiRequest(`/revenue/overview${params.toString() ? `?${params.toString()}` : ''}`);
  },

  // Get all revenue transactions
  getTransactions: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    return adminApiRequest(`/revenue${queryParams ? `?${queryParams}` : ''}`);
  },

  // Get single revenue transaction
  getTransaction: async (transactionId) => {
    return adminApiRequest(`/revenue/${transactionId}`);
  },

  // Create revenue transaction (deposit/withdrawal)
  createTransaction: async (transactionData) => {
    return adminApiRequest('/revenue', {
      method: 'POST',
      body: JSON.stringify(transactionData),
    });
  },

  // Update revenue transaction
  updateTransaction: async (transactionId, transactionData) => {
    return adminApiRequest(`/revenue/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(transactionData),
    });
  },

  // Delete revenue transaction
  deleteTransaction: async (transactionId) => {
    return adminApiRequest(`/revenue/${transactionId}`, {
      method: 'DELETE',
    });
  },

  // Get revenue statistics
  getStats: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return adminApiRequest(`/revenue/stats/summary${params.toString() ? `?${params.toString()}` : ''}`);
  },
};

// Coupon Services
export const couponService = {
  // Validate coupon code
  validateCoupon: async (couponCode) => {
    const response = await apiRequest(`/coupons/validate/${couponCode}`);
    return { success: true, coupon: response.data || response, message: response.message || 'Coupon validated' };
  },

  // Get available coupons
  getAvailableCoupons: async () => {
    const response = await apiRequest('/coupons/available');
    return { success: true, coupons: response.data || response, message: response.message || 'Coupons loaded' };
  },

  // Apply coupon to cart
  applyCoupon: async (couponCode, cartId) => {
    return apiRequest('/coupons/apply', {
      method: 'POST',
      body: JSON.stringify({ couponCode, cartId }),
    });
  },

  // Remove coupon from cart
  removeCoupon: async (cartId) => {
    return apiRequest(`/coupons/remove/${cartId}`, {
      method: 'DELETE',
    });
  },

  // Get user coupon history
  getCouponHistory: async () => {
    const response = await apiRequest('/coupons/history');
    return { success: true, history: response.data || response, message: response.message || 'Coupon history loaded' };
  },
};

/**
 * Admin promo coupons — matches backend:
 * | Method | Path |
 * | GET    | /api/admin/coupons  (query: page, limit, isActive, search) |
 * | POST   | /api/admin/coupons |
 * | PUT    | /api/admin/coupons/:couponId |
 * | DELETE | /api/admin/coupons/:couponId (hard delete) |
 * Base URL already includes /api; paths start with /admin/coupons.
 */
export const adminCouponService = {
  /** @param {Object} [params] - page, limit, isActive (boolean), search */
  listCoupons: async (params = {}) => {
    const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
    const q = new URLSearchParams(
      entries.map(([k, v]) => [k, typeof v === 'boolean' ? String(v) : v])
    ).toString();
    return adminApiRequest(`/admin/coupons${q ? `?${q}` : ''}`);
  },
  createCoupon: async (payload) => {
    return adminApiRequest('/admin/coupons', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updateCoupon: async (couponId, payload) => {
    return adminApiRequest(`/admin/coupons/${encodeURIComponent(couponId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  deleteCoupon: async (couponId) => {
    return adminApiRequest(`/admin/coupons/${encodeURIComponent(couponId)}`, {
      method: 'DELETE',
    });
  },
};

// Payment Services
export const paymentService = {
  // Create payment intent
  createPaymentIntent: async (amount, currency = 'PKR') => {
    return apiRequest('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify({ amount, currency }),
    });
  },

  // Confirm payment
  confirmPayment: async (paymentIntentId) => {
    return apiRequest('/payments/confirm', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId }),
    });
  },

  // Get payment methods
  getPaymentMethods: async () => {
    const response = await apiRequest('/payments/methods');
    return { success: true, methods: response.data || response, message: response.message || 'Payment methods loaded' };
  },

  // Add payment method
  addPaymentMethod: async (paymentMethodData) => {
    return apiRequest('/payments/methods', {
      method: 'POST',
      body: JSON.stringify(paymentMethodData),
    });
  },

  // Remove payment method
  removePaymentMethod: async (paymentMethodId) => {
    return apiRequest(`/payments/methods/${paymentMethodId}`, {
      method: 'DELETE',
    });
  },

  // Process payment
  processPayment: async (paymentData) => {
    return apiRequest('/payments/process', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  // Get payment history
  getPaymentHistory: async () => {
    const response = await apiRequest('/payments/history');
    return { success: true, history: response.data || response, message: response.message || 'Payment history loaded' };
  },
};

/** Parent id from category row (parentCategory or parent). */
function getCategoryParentId(cat) {
  if (!cat) return null;
  const raw = cat.parentCategory ?? cat.parent ?? null;
  if (raw == null) return null;
  if (typeof raw === 'object') {
    const id = raw._id ?? raw.id;
    return id != null ? String(id).trim() : null;
  }
  const s = String(raw).trim();
  return s && s !== 'undefined' && s !== 'null' ? s : null;
}

function normalizeCategoryList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.categories)) return payload.categories;
  return [];
}

function filterSubcategoriesByParent(allCategories, parentId) {
  const parentStr = String(parentId).trim();
  if (!parentStr) return [];
  return allCategories.filter((cat) => getCategoryParentId(cat) === parentStr);
}

// Category Services
export const categoryService = {
  // ✅ FIXED: Get all categories
  getCategories: async () => {
    const response = await apiRequest('/categories');
    return { 
      success: response.success, 
      categories: response.data || response, 
      message: response.message || 'Categories loaded successfully' 
    };
  },

  // ✅ FIXED: Get category by ID
  getCategory: async (categoryId) => {
    const response = await apiRequest(`/categories/${categoryId}`);
    return { 
      success: response.success, 
      category: response.data || response, 
      message: response.message || 'Category loaded successfully' 
    };
  },

  // ✅ FIXED: Get category by slug (SEO-friendly URLs)
  getCategoryBySlug: async (slug) => {
    const response = await apiRequest(`/categories/slug/${slug}`);
    return { 
      success: response.success, 
      category: response.data || response, 
      message: response.message || 'Category loaded successfully' 
    };
  },

  // Subcategories: admin route exists; public /categories/:id/subcategories does not on sheen-BE
  getSubcategories: async (categoryId) => {
    const parentId = String(categoryId ?? '').trim();
    if (!parentId) {
      return { success: true, subcategories: [], message: 'No category id' };
    }

    const hasAdminToken =
      localStorage.getItem('adminToken') || localStorage.getItem('authToken');

    if (hasAdminToken) {
      try {
        const response = await adminApiRequest(
          `/admin/categories/subcategories/${parentId}`
        );
        const list = normalizeCategoryList(response.data ?? response);
        if (response.success !== false) {
          return {
            success: true,
            subcategories: list,
            message: response.message || 'Subcategories loaded successfully',
          };
        }
      } catch {
        // Fall through to filter from GET /categories
      }
    }

    try {
      const response = await apiRequest('/categories', { silentLog: true });
      const all = normalizeCategoryList(response.data ?? response);
      return {
        success: true,
        subcategories: filterSubcategoriesByParent(all, parentId),
        message: 'Subcategories loaded successfully',
      };
    } catch (error) {
      return {
        success: false,
        subcategories: [],
        message: error?.message || 'Failed to load subcategories',
      };
    }
  },

  // ✅ FIXED: Get products by category with pagination
  getProductsByCategory: async (categoryId, page = 1, limit = 100) => {
    return productService.getProducts({
      category: categoryId,
      page,
      limit,
    });
  },
};

// Search Services
export const searchService = {
  // Search products with advanced filters (same public catalog merge as getProducts)
  searchProducts: async (query, filters = {}) => {
    return productService.getProducts({
      search: query || filters.search,
      category: filters.category,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      gender: filters.gender,
      sort: filters.sort,
      order: filters.order,
      limit: filters.limit,
      page: filters.page,
    });
  },

  // Get search suggestions
  getSearchSuggestions: async (query) => {
    const response = await apiRequest(`/search/suggestions?q=${encodeURIComponent(query)}`);
    return { success: true, suggestions: response.data || response, message: response.message || 'Suggestions loaded' };
  },

  // Get popular searches
  getPopularSearches: async () => {
    const response = await apiRequest('/search/popular');
    return { success: true, searches: response.data || response, message: response.message || 'Popular searches loaded' };
  },

  // Get search history
  getSearchHistory: async () => {
    const response = await apiRequest('/search/history');
    return { success: true, history: response.data || response, message: response.message || 'Search history loaded' };
  },

  // Clear search history
  clearSearchHistory: async () => {
    return apiRequest('/search/history', {
      method: 'DELETE',
    });
  },
};

// Review Services
export const reviewService = {
  // Get product reviews
  getProductReviews: async (productId) => {
    const response = await apiRequest(`/reviews/product/${productId}`);
    return { success: true, reviews: response.data || response, message: response.message || 'Reviews loaded' };
  },

  // Add review
  addReview: async (productId, reviewData) => {
    return apiRequest(`/reviews/product/${productId}`, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  },

  // Admin: Create review manually (uses admin token)
  createAdminReview: async (productId, reviewData) => {
    return adminApiRequest('/admin/reviews', {
      method: 'POST',
      body: JSON.stringify({ product: productId, ...reviewData }),
    });
  },

  // Update review
  updateReview: async (reviewId, reviewData) => {
    return apiRequest(`/reviews/${reviewId}`, {
      method: 'PUT',
      body: JSON.stringify(reviewData),
    });
  },

  // Delete review
  deleteReview: async (reviewId) => {
    return apiRequest(`/reviews/${reviewId}`, {
      method: 'DELETE',
    });
  },

  // Rate review as helpful
  rateReviewHelpful: async (reviewId) => {
    return apiRequest(`/reviews/${reviewId}/helpful`, {
      method: 'POST',
    });
  },

  // Report review
  reportReview: async (reviewId, reason) => {
    return apiRequest(`/reviews/${reviewId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// Notification Services
export const notificationService = {
  // Get user notifications
  getNotifications: async () => {
    const response = await apiRequest('/notifications');
    return { success: true, notifications: response.data || response, message: response.message || 'Notifications loaded' };
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    return apiRequest('/notifications/mark-all-read', {
      method: 'PUT',
    });
  },

  // Delete notification
  deleteNotification: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  },

  // Subscribe to notifications
  subscribeToNotifications: async () => {
    return apiRequest('/notifications/subscribe', {
      method: 'POST',
    });
  },

  // Unsubscribe from notifications
  unsubscribeFromNotifications: async () => {
    return apiRequest('/notifications/unsubscribe', {
      method: 'POST',
    });
  },
};

// Settings Service (Admin) - localStorage fallback when backend route doesn't exist
export const settingsService = {
  getSite: async () => {
    try {
      return await adminApiRequest('/admin/settings/site');
    } catch (e) {
      const stored = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
      return stored ? { success: true, data: JSON.parse(stored) } : { success: false, data: null };
    }
  },
  getNotifications: async () => {
    try {
      return await adminApiRequest('/admin/settings/notifications');
    } catch (e) {
      const stored = localStorage.getItem('sheenwear_notification_settings');
      return stored ? { success: true, data: JSON.parse(stored) } : { success: false, data: null };
    }
  },
  getSecurity: async () => {
    try {
      return await adminApiRequest('/admin/settings/security');
    } catch (e) {
      const stored = localStorage.getItem('sheenwear_security_settings');
      return stored ? { success: true, data: JSON.parse(stored) } : { success: false, data: null };
    }
  },
  getBackup: async () => {
    try {
      return await adminApiRequest('/admin/settings/backup');
    } catch (e) {
      const stored = localStorage.getItem('sheenwear_backup_settings');
      return stored ? { success: true, data: JSON.parse(stored) } : { success: false, data: null };
    }
  },
  saveSite: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/site', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) {
        const serverSite =
          res?.data && typeof res.data === 'object' ? res.data : null;
        const toStore = serverSite ? { ...data, ...serverSite } : data;
        localStorage.setItem(SITE_SETTINGS_STORAGE_KEY, JSON.stringify(toStore));
      }
      return res;
    } catch (e) {
      localStorage.setItem(SITE_SETTINGS_STORAGE_KEY, JSON.stringify(data));
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
      };
    }
  },
  saveNotifications: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/notifications', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) localStorage.setItem('sheenwear_notification_settings', JSON.stringify(data));
      return res;
    } catch (e) {
      localStorage.setItem('sheenwear_notification_settings', JSON.stringify(data));
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
      };
    }
  },
  saveSecurity: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/security', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) localStorage.setItem('sheenwear_security_settings', JSON.stringify(data));
      return res;
    } catch (e) {
      localStorage.setItem('sheenwear_security_settings', JSON.stringify(data));
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
      };
    }
  },
  saveBackup: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/backup', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) localStorage.setItem('sheenwear_backup_settings', JSON.stringify(data));
      return res;
    } catch (e) {
      localStorage.setItem('sheenwear_backup_settings', JSON.stringify(data));
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
      };
    }
  },
  createBackup: async () => {
    return adminApiRequest('/admin/settings/backup/create', {
      method: 'POST',
    });
  },
  clearCache: async () => {
    return adminApiRequest('/admin/settings/cache/clear', {
      method: 'POST',
    });
  },
  getSellerAddress: async () => {
    try {
      return await adminApiRequest('/admin/settings/seller-address');
    } catch (e) {
      const stored = localStorage.getItem('sheenwear_seller_address');
      return stored ? { success: true, data: JSON.parse(stored) } : { success: false, data: null };
    }
  },
  saveSellerAddress: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/seller-address', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) localStorage.setItem('sheenwear_seller_address', JSON.stringify(data));
      return res;
    } catch (e) {
      localStorage.setItem('sheenwear_seller_address', JSON.stringify(data));
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
      };
    }
  },

  /** Public: WhatsApp + free delivery promo for checkout (no auth). See docs/BACKEND_CONTACT_WHATSAPP_SETTINGS.md */
  getContactChannels: async () => {
    try {
      const res = await apiRequest('/settings/contact');
      const d = res?.data;
      if (d && typeof d === 'object') {
        return { ...res, data: normalizeContactChannelsData(d) };
      }
      return res;
    } catch {
      const raw = localStorage.getItem(CONTACT_CHANNELS_STORAGE_KEY);
      if (raw) {
        try {
          return { success: true, data: normalizeContactChannelsData(JSON.parse(raw)) };
        } catch {
          /* ignore */
        }
      }
      const wa = getWhatsAppConfig();
      const n = wa.businessNumber || '';
      return {
        success: true,
        data: normalizeContactChannelsData({
          whatsappChatNumber: n,
          whatsappOrderNumber: n,
        }),
      };
    }
  },

  getContactChannelsAdmin: async () => {
    try {
      const res = await adminApiRequest('/admin/settings/contact');
      if (res?.success === false && res?.status === 404) {
        const raw = localStorage.getItem(CONTACT_CHANNELS_STORAGE_KEY);
        if (raw) {
          try {
            return { success: true, data: normalizeContactChannelsData(JSON.parse(raw)) };
          } catch {
            /* ignore */
          }
        }
      }
      const d = res?.data;
      if (d && typeof d === 'object') {
        const prev = (() => {
          try {
            const r = localStorage.getItem(CONTACT_CHANNELS_STORAGE_KEY);
            return r ? JSON.parse(r) : {};
          } catch {
            return {};
          }
        })();
        const merged = normalizeContactChannelsData({ ...prev, ...d });
        localStorage.setItem(CONTACT_CHANNELS_STORAGE_KEY, JSON.stringify(merged));
      }
      return res;
    } catch {
      const raw = localStorage.getItem(CONTACT_CHANNELS_STORAGE_KEY);
      if (raw) {
        try {
          return { success: true, data: normalizeContactChannelsData(JSON.parse(raw)) };
        } catch {
          /* ignore */
        }
      }
      return { success: true, data: normalizeContactChannelsData({}) };
    }
  },

  saveContactChannels: async (data) => {
    try {
      const res = await adminApiRequest('/admin/settings/contact', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res?.success !== false) {
        persistContactChannelsLocal(data);
      }
      return res;
    } catch (e) {
      persistContactChannelsLocal(data);
      return {
        success: false,
        cachedLocally: true,
        error: e?.message || 'network_error',
        message:
          'Server could not save. A copy was kept only on this device. Check connection and try again.',
        data: normalizeContactChannelsData(data),
      };
    }
  },
};

/** Main-admin Cloudinary maintenance (unused/orphan assets). */
export const cloudinaryAdminService = {
  scanUnusedImages: async () => {
    return adminApiRequest('/admin/cloudinary/unused-images', {
      method: 'GET',
    });
  },
  deleteUnusedImages: async (publicIds = []) => {
    return adminApiRequest('/admin/cloudinary/unused-images/delete', {
      method: 'POST',
      body: JSON.stringify({ publicIds }),
    });
  },
};

/** Admin: aggregate product page view analytics (dashboard + analytics page). */
export const analyticsAdminService = {
  getProductViewsSummary: async () => {
    try {
      const res = await adminApiRequest('/admin/analytics/product-views', { method: 'GET' });
      const d = res?.data ?? res;
      const totalViews = Number(
        d?.totalViews ?? d?.totalProductViews ?? d?.total ?? d?.sum ?? d?.views ?? 0
      );
      const topProducts = Array.isArray(d?.topProducts)
        ? d.topProducts
        : Array.isArray(d?.topByViews)
        ? d.topByViews
        : [];
      return {
        success: res?.success !== false && res?.status !== 404,
        totalViews: Number.isFinite(totalViews) ? totalViews : 0,
        topProducts,
        raw: d,
      };
    } catch {
      return { success: false, totalViews: 0, topProducts: [], raw: null };
    }
  },
};

// Export all services
const apiServices = {
  authService,
  userService,
  productService,
  orderService,
  accountService,
  revenueService,
  paymentService,
  categoryService,
  reviewService,
  notificationService,
  cartService,
  wishlistService,
  recentVisitsService,
  searchService,
  couponService,
  adminCouponService,
  settingsService,
  cloudinaryAdminService,
  analyticsAdminService,
};

export default apiServices;
export { adminApiRequest }; 